import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '@app/env-service';

export type ClientPurchaseOrderType = {
  affects_the_inventory :boolean;
  apply_pricing_structure :boolean;
  createdAt :number;
  createdBy :number;
  deletedAt :number;
  deletedBy :number;
  drop_ship :boolean;
  id :number;
  include_in_sales_reports :boolean;
  label :string;
  locked :boolean;
  status_string :string;
  updatedAt :number;
  updatedBy :number;
};

type ClientPurchaseOrderTypeResponse = {
  data :ClientPurchaseOrderType[];
  meta :{
    current_page :number;
    from :number;
    last_page :number;
    path :string;
    per_page :number;
    to :number;
    total :number;
  };
};

@Injectable({
  providedIn: 'root',
})
export class ClientPurchaseOrderTypeService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  private readonly _client_purchase_order_type = new BehaviorSubject<any>(<any>[]);
  public client_purchase_order_types$ =
    this._client_purchase_order_type.asObservable();

  private readonly _activeRecord = new BehaviorSubject<any>(<any>{});
  public activeRecord$ = this._activeRecord.asObservable();

  public gettingRecords :boolean = false;

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  private params :any = {
    per_page: 20,
    page: 1,
  };

  constructor(private http :HttpClient) {}

  getClientPurchaseOrderTypes(params) :Subscription {
    this.gettingRecords = true;
    this._loading.next(true);

    return this.http
      .get(`${this.urlV2}/manage/client-purchase-order-type`, {
        params: params,
      })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._loading.next(false);
        this._client_purchase_order_type.next(resp);
      });
  }

  get(params = {}) {
    return this.http.get<ClientPurchaseOrderTypeResponse>(
      `${this.urlV2}/manage/client-purchase-order-type`,
      {
        params,
      },
    );
  }

  getAllAsync() :Observable<any> {
    return this.http.get(`${this.urlV2}/manage/client-purchase-order-type`);
  }

  getById(clientPurchaseOrderTypeId :number) :Observable<any> {
    return this.http.get(
      `${this.urlV2}/manage/client-purchase-order-type/${clientPurchaseOrderTypeId}`,
    );
  }

  searchClientPurchaseOrderType(params :any) :Observable<any> {
    return this.http.get(`${this.urlV2}/manage/client-purchase-order-type`, {
      params: params,
    });
  }

  refreshList() :void {
    this.getClientPurchaseOrderTypes(this.params);
  }

  setParams(params :any) :void {
    this.params = params;
  }

  saveClientPurchaseOrderType(payload :any) :Observable<any> {
    return this.http
      .post(`${this.urlV2}/manage/client-purchase-order-type`, payload)
      .pipe(
        tap((resp :any) => {
          setTimeout(() => {
            const client_purchase_order_types :any =
              this._client_purchase_order_type.getValue();
            if (client_purchase_order_types && client_purchase_order_types.data) {
              client_purchase_order_types.data.splice(0, 0, resp);
              this._client_purchase_order_type.next(client_purchase_order_types);
            }
          });
        }),
      );
  }

  updateClientPurchaseOrderType(
    payload :any,
    clientPurchaseOrderTypeId :number,
  ) :Observable<any> {
    return this.http
      .put(
        `${this.urlV2}/manage/client-purchase-order-type/${clientPurchaseOrderTypeId}`,
        payload,
      )
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  deleteClientPurchaseOrderType(
    clientPurchaseOrderTypeId :number,
  ) :Observable<any> {
    return this.http
      .delete(
        `${this.urlV2}/manage/client-purchase-order-type/${clientPurchaseOrderTypeId}`,
      )
      .pipe(
        tap((resp :any) => {
          const client_purchase_order_types :any =
            this._client_purchase_order_type.getValue();
          const clientPurchaseOrderTypeIndex =
            client_purchase_order_types.data.findIndex(
              (row) => row.id == clientPurchaseOrderTypeId,
            );
          client_purchase_order_types.data.splice(clientPurchaseOrderTypeIndex, 1);
          this._client_purchase_order_type.next(client_purchase_order_types);
        }),
      );
  }

  setActiveById(id :number) :void {
    this.gettingRecords = true;

    const record :any = this._client_purchase_order_type.getValue();
    if (record.data) {
      const clientPurchaseOrderType = record.data.find((row) => {
        return row.id == id;
      });
      if (clientPurchaseOrderType) {
        this.setActive(clientPurchaseOrderType);

        return;
      }
    }
    this.getById(id).subscribe((clientPurchaseOrderType) => {
      this.setActive(clientPurchaseOrderType);
    });
  }

  setActive(po :any) :void {
    this.gettingRecords = false;

    this._activeRecord.next(po);
  }

  getActive() :Observable<any> {
    return this._activeRecord.getValue();
  }

  tryUpdateOrAddResp(resp) :void {
    const client_purchase_order_types :any =
      this._client_purchase_order_type.getValue();
    if (!client_purchase_order_types || !client_purchase_order_types.data) {
      return;
    }
    const clientPurchaseOrderTypeIndex = client_purchase_order_types.data.findIndex(
      (row) => row.id == resp.id,
    );

    client_purchase_order_types.data[clientPurchaseOrderTypeIndex] = resp;

    this._client_purchase_order_type.next(client_purchase_order_types);
  }
}
